// /templates/4.0/checkout/components/booking-summary/multi-booking/multi-booking-accordion-item.tpl
import { toggle } from '../../utils/ui-helpers.js';
import { openAccordionItem } from '../third-party/accordion.js';
import { getActiveBookingSummary } from '../checkout-summary.js';
import { isStage3Valid } from '../../validation.js';
import { showModal } from '../ui.js';
import { onStageComplete, handleInputChange } from '../../event-handlers.js';
import { isMultisiteEnabled } from '../../state.js';

// Templates
let readOnlySummaryTemp, activeSummaryTemp;

// Other important elements
let container, mbHeader, bookingNumBadge;

let hasStarted = false;

// note: put this here so it's easy to change
const BOOKING_ITEM_IDENTIFIER = 'atb-mb-booking-summary-item-';
const ACCORDION_ID 			  = '#atb-multi-booking-accordion';

export function init() {	
	if (hasStarted) return;

	// Turn on the multi-booking question
	toggle(document.getElementById('atb-multi-booking-question'), true);

	// Lazy cache all relevant UI - doing this outside of init() causes an error when querying for mbHeader
	readOnlySummaryTemp = document.getElementById('atb-mb-readonly-summary-item-template');
	activeSummaryTemp   = document.getElementById('atb-mb-active-summary-item-template');
	
	container           = document.getElementById('atb-multi-booking-accordion');
	mbHeader            = document.getElementById('atb-multi-booking-header');
	bookingNumBadge     = mbHeader.querySelector('.atb-num-bookings-badge');

	// Get summary items - this is more of a formality, there shouldn't be any existing items
	let bookingSummaryItemArray = document.querySelectorAll('#atb-multi-booking-accordion .atb-mb-booking-item.accordion-item');
	toggle(mbHeader, bookingSummaryItemArray.length > 1, mbHeader.dataset.display);

	attachEventHandlers();

	hasStarted = true;
}


function attachEventHandlers() {
    // Multiple-booking "Delete" handler
    document.addEventListener('click', handleDeleteBooking);

    // Multiple-booking "Add" handler
    document.addEventListener('click', handleAddBooking);

	console.log('Multi-booking Module: Event handlers attached');
}


export function renderCompletePageAccordion(bookings) {
	if(!hasStarted) {
		init();
	}
	
	container.innerHTML = '';
	bookings.forEach((bookingData, index) => {
		createReadOnlySummary({
			title: `Booking ${index + 1}`,
			bookingData,
			index,
			doShowDeleteButton: false,
			doShowTotalCostRow: true,
			doUseCompletePageStyle: true // we want to hide the multi-booking UI elements (like the delete button) on the booking complete page
			//forceHideMultiUI: true // we want to hide the multi-booking UI elements (like the delete button) on the booking complete page
		});
	});

	openAccordionItem(ACCORDION_ID, 0);
}

export function renderBookingAccordion(bookings, currentBookingIndex) {
	if(!hasStarted) {
		init();
	}

	container.innerHTML = '';

	let currentSummary = null;
	bookings.forEach((bookingData, index) => {
		const isCurrent = index === currentBookingIndex;

		if (isCurrent) {
			currentSummary = createNewBookingSummary({
				bookingData,
				index,
				doShowItemHeader: bookings.length > 1
			});
		} else {
			createReadOnlySummary({
				title: `Booking ${index + 1}`,
				bookingData,
				index
			});
		}
	});

	openAccordionItem(ACCORDION_ID, currentBookingIndex);

	syncBookingNumBadge(bookings.length);
	toggle(mbHeader, bookings.length > 1, mbHeader.dataset.display);

	getActiveBookingSummary();
}


function syncBookingNumBadge(bookingNumber) {
	bookingNumBadge.textContent = bookingNumber;
}


function createNewBookingSummary({bookingData, index, doShowItemHeader, overrideTitle = null}) {
	return createSummaryAccordionItem({
		title: overrideTitle !== null ? overrideTitle : 'Current Booking',
		bookingData,
		index,
		doShowDeleteButton: false,
		doShowTotalCostRow: false,
		doShowItemHeader,
		templateFrag: activeSummaryTemp
	});
}

function createReadOnlySummary({
	title, 
	bookingData, 
	index, 
	doShowDeleteButton = true, 
	doShowTotalCostRow = true, 
	doUseCompletePageStyle = false
}) {
	return createSummaryAccordionItem({
		title, 
		bookingData, 
		index, 
		doShowDeleteButton,
		doShowTotalCostRow,
		templateFrag: readOnlySummaryTemp,
		doUseCompletePageStyle
	});
}

function createSummaryAccordionItem({ 
	title,
	bookingData,
	index,
	templateFrag,
	accordionId = 'atb-multi-booking-accordion',	
	doShowDeleteButton = false,
	doShowTotalCostRow = false,
	doShowItemHeader = true,
	doUseCompletePageStyle = false
}) {
  	const clone = templateFrag.content.cloneNode(true);

	// Get the accordion item's root element inside the template
	const accordionItem        = clone.querySelector('.accordion-item');
	const button               = clone.querySelector('.accordion-button');
	const collapse             = clone.querySelector('.accordion-collapse');
	const bookingSummaryRoot   = clone.querySelector('.accordion-body');

	accordionItem.dataset.bookingIndex = index;
	const collapseId = `${BOOKING_ITEM_IDENTIFIER}${index}`;

	button.textContent = title;
	button.setAttribute('data-bs-target', `#${collapseId}`);
	button.setAttribute('aria-controls', collapseId);
	collapse.setAttribute('data-bs-parent', `#${accordionId}`);
	collapse.id = collapseId;

	toggle(clone.querySelector('.atb-mb-booking-item-header'), doShowItemHeader);
	
	buildBookingSummary({
		tmpRootEl: bookingSummaryRoot, 
		bookingData, 
		bookingIndex: index, 
		doShowDeleteButton,
		doShowTotalCostRow,
		doUseCompletePageStyle
	});
	container.append(clone);

	return accordionItem;
}

/**
 * Builds the booking summary UI given a booking data object
 * @param {*} tmpRootEl - The root of the template fragment
 * @param {*} bookingData - Plain JS object representing the booking UI data
 * @param {*} bookingIndex - 
 * @param {boolean} doShowMultiUI - A boolean value used to toggle the delete button and accordion header
 */
function buildBookingSummary({
	tmpRootEl, 
	bookingData, 
	bookingIndex, 
	doShowTotalCostRow = false,
	doShowDeleteButton = false,
	doUseCompletePageStyle = false
}) {

	// TODO: pass the total cost of the booking if it exists
	const {
		unitName,
		unitThumbnail = null,
		subUnitName = null,
		unitAddress,
		siteName = '',
		arrivalDateLongFormat,
		departureDateLongFormat,
		lengthOfStay,
		totalCostFloat,
		totalCostString,
		hasDiscount,
		totalNormalString
	} = bookingData;

	// Setup booking delete button
	const deleteButton = tmpRootEl.querySelector('[data-input="delete_booking"]');
	deleteButton.setAttribute('data-booking-index', bookingIndex);

	// Don't show the delete button - typically used when we only have one booking summary item
	toggle(deleteButton, doShowDeleteButton);

	// Unit name
	tmpRootEl.querySelector('[data-summary-unit-name]').textContent = unitName;

	// Site name (only shown when the multisite module is enabled)
	const siteEl = tmpRootEl.querySelector('[data-summary-unit-site]');
	siteEl.textContent = siteName;
	toggle(siteEl, isMultisiteEnabled() && !!siteName);

	// Thumbnail
	const thumbnailContainer =
		tmpRootEl.querySelector('[data-checkout-thumbmail-container]');
	const thumbnailImg =
		tmpRootEl.querySelector('[data-checkout-thumbnail]');

	if (unitThumbnail) {
		thumbnailImg.src = unitThumbnail;
		thumbnailImg.alt = `Image of ${unitName}`;
		toggle(thumbnailContainer, true);
	} else {
		toggle(thumbnailContainer, false);
	}

	// Sub-unit name
	const subUnitEl =
		tmpRootEl.querySelector('[data-summary-sub-unit-name]');

	if (subUnitName) {
		subUnitEl.textContent = subUnitName;
	} else {
		toggle(subUnitEl, false);
	}

	tmpRootEl.querySelector('[data-summary-unit-location]').textContent =
		unitAddress;

	// Arrival / departure dates
	tmpRootEl.querySelector('[data-summary-arrival-date]').textContent =
		arrivalDateLongFormat;

	tmpRootEl.querySelector('[data-summary-departure-date]').textContent =
		departureDateLongFormat;

	// Period / length of stay
	tmpRootEl.querySelector('[data-summary-period]').textContent =
		lengthOfStay;

	// Add the cost of the booking to the end of the template
	if (doShowTotalCostRow && totalCostFloat !== 0)
		tmpRootEl.querySelector('.atb-booking-details').appendChild(createTotalCostRow(totalCostString, hasDiscount, totalNormalString, doUseCompletePageStyle));
}

/**
 * 
 * @param {*} totalText 
 * @returns 
 */
function createTotalCostRow(totalText = '£0', includeTotalBeforeDiscount = false, totalBeforeDiscount = '', useCompletePageMode = false) {
    const row = document.createElement('div');
    row.setAttribute('data-summary-total-cost-row', '');
    row.className = `row px-0 fw-medium pb-2`;
	row.className += useCompletePageMode ? ' text-lg pt-3 mt-3 atb-summary-row-border' : ' mt-2';

    const labelCol = document.createElement('div');
    labelCol.className = 'col-6 px-0';
    labelCol.textContent = 'Total';

	const costCol = document.createElement('div');
    costCol.className = 'col-6 px-0 justify-content-end d-flex';

	if (includeTotalBeforeDiscount && totalBeforeDiscount !== '')
		costCol.innerHTML += `<span class="text-grey text-decoration-line-through me-1">${totalBeforeDiscount}</span>${totalText}`
	else
    	costCol.innerHTML = `${totalText}`;

    row.appendChild(labelCol);
	row.appendChild(costCol);

    return row;
}


// function createDiscountCostRow() {
// 	<div class="atb-summary-discount-row atb-discount-amount row px-0 text-sm text-dark-green mb-1">
// 		<div class="col-6 px-0 d-flex align-items-center">Discount <span class="atb-chip discount">£175 OFF</span></div>
// 		<div class="col-6 px-0 text-end">-<span id="atb-summary-discount-cost">£175</span></div>
// 	</div>
// }


function handleAddBooking(event) {	
	const btn = event.target.closest('[data-input="add_booking"]');
	if (!btn) return;

	if (!isStage3Valid())       
		return;
		
	onStageComplete(new CustomEvent("StageComplete", { detail: { stage: "party" }}));

	showModal(
		'Are you sure?', 
		'<p>You will be taken to the beginning of the booking process.</p>', 
		{ 
			buttonCallback: () => handleInputChange(btn), 
			buttonText: "Add booking" 
		}
	);
}


function handleDeleteBooking(event) {
	const btn = event.target.closest('[data-input="delete_booking"]');                
	if (!btn) return;

	const accordionItem = document.querySelector(`[data-booking-index="${btn.dataset.bookingIndex}"] .accordion-collapse`);
	const clone = accordionItem.cloneNode(true);
	clone.querySelectorAll('button, .atb-cost-accordions, #atb-summary-discount-code-row, hr:not(.before-costs), #atb-payment-plan-selector-summary').forEach(el => el.remove());
	clone.querySelector('atb-')

	showModal(
		'Are you sure?', 
		`<p>Clicking continue will remove the following booking:</p> ${clone.innerHTML}`, 
		{ 
			buttonCallback: () => handleInputChange(btn), 
			buttonText: "Delete your booking" 
		}
	);
}